import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const NewsManagement = () => {
  const { isSuperUser } = useAuth();
  const [newsArticles, setNewsArticles] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [currentArticle, setCurrentArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    author: '',
    category: '',
    imageUrl: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNewsArticles();
  }, []);

  const loadNewsArticles = () => {
    setLoading(true);
    try {
      const savedNews = localStorage.getItem('newsArticles');
      if (savedNews) {
        setNewsArticles(JSON.parse(savedNews));
      }
    } catch (error) {
      console.error('Error loading news articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (type, article = null) => {
    if (!isSuperUser()) {
      setError('Only Super Users can manage news articles');
      return;
    }
    
    setDialogType(type);
    setCurrentArticle(article);
    
    if (article) {
      setFormData({
        title: article.title,
        summary: article.summary,
        content: article.content,
        author: article.author,
        category: article.category,
        imageUrl: article.imageUrl,
        date: article.date
      });
    } else {
      setFormData({
        title: '',
        summary: '',
        content: '',
        author: '',
        category: 'General',
        imageUrl: '',
        date: new Date().toISOString().split('T')[0]
      });
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
    setSuccess('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (!isSuperUser()) {
      setError('Only Super Users can manage news articles');
      return;
    }
    
    // Validate form data
    if (!formData.title || !formData.summary || !formData.content || !formData.imageUrl) {
      setError('Title, summary, content, and image URL are required');
      return;
    }
    
    if (dialogType === 'add') {
      const newArticle = {
        id: Date.now(),
        title: formData.title,
        summary: formData.summary,
        content: formData.content,
        author: formData.author,
        category: formData.category || 'General',
        imageUrl: formData.imageUrl,
        date: formData.date
      };
      
      const updatedNews = [...newsArticles, newArticle];
      setNewsArticles(updatedNews);
      localStorage.setItem('newsArticles', JSON.stringify(updatedNews));
      setSuccess('News article added successfully');
    } else if (dialogType === 'edit') {
      const updatedNews = newsArticles.map(article => 
        article.id === currentArticle.id 
          ? { 
              ...article, 
              title: formData.title,
              summary: formData.summary,
              content: formData.content,
              author: formData.author,
              category: formData.category || 'General',
              imageUrl: formData.imageUrl,
              date: formData.date
            } 
          : article
      );
      
      setNewsArticles(updatedNews);
      localStorage.setItem('newsArticles', JSON.stringify(updatedNews));
      setSuccess('News article updated successfully');
    }
    
    // Close dialog after successful submission
    setTimeout(() => {
      handleCloseDialog();
    }, 1500);
  };

  const handleDelete = (id) => {
    if (!isSuperUser()) {
      setError('Only Super Users can manage news articles');
      return;
    }
    
    const updatedNews = newsArticles.filter(article => article.id !== id);
    setNewsArticles(updatedNews);
    localStorage.setItem('newsArticles', JSON.stringify(updatedNews));
    setSuccess('News article deleted successfully');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccess('');
    }, 3000);
  };

  if (!isSuperUser()) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <LockIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          Restricted Access
        </Typography>
        <Typography variant="body1">
          News and article management is restricted to Super Users only.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">News & Articles Management</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
        >
          Add Article
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <Grid container spacing={2}>
        {newsArticles.map(article => (
          <Grid item xs={12} sm={6} md={4} key={article.id}>
            <Card>
              <CardMedia
                component="img"
                height="140"
                image={article.imageUrl}
                alt={article.title}
              />
              <CardContent>
                <Typography variant="h6" component="div" gutterBottom noWrap>
                  {article.title}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Chip 
                    label={article.category} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(article.date).toLocaleDateString()}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}>
                  {article.summary}
                </Typography>
              </CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                <IconButton 
                  color="primary"
                  onClick={() => handleOpenDialog('edit', article)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  color="error"
                  onClick={() => handleDelete(article.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
        {newsArticles.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography>No news articles found</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
      
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogType === 'add' ? 'Add New Article' : 'Edit Article'}
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <TextField
              autoFocus
              margin="dense"
              name="title"
              label="Article Title"
              type="text"
              fullWidth
              value={formData.title}
              onChange={handleChange}
              required
            />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="author"
                  label="Author"
                  type="text"
                  fullWidth
                  value={formData.author}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  name="date"
                  label="Publication Date"
                  type="date"
                  fullWidth
                  value={formData.date}
                  onChange={handleChange}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
            
            <FormControl fullWidth margin="dense">
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <MenuItem value="General">General</MenuItem>
                <MenuItem value="Techniques">Techniques</MenuItem>
                <MenuItem value="Sustainability">Sustainability</MenuItem>
                <MenuItem value="Market">Market</MenuItem>
                <MenuItem value="Research">Research</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              margin="dense"
              name="summary"
              label="Article Summary"
              type="text"
              fullWidth
              multiline
              rows={2}
              value={formData.summary}
              onChange={handleChange}
              required
              helperText="A brief summary that appears in the article list"
            />
            
            <TextField
              margin="dense"
              name="content"
              label="Article Content"
              type="text"
              fullWidth
              multiline
              rows={6}
              value={formData.content}
              onChange={handleChange}
              required
              helperText="Use double line breaks (press Enter twice) to create new paragraphs"
            />
            
            <TextField
              margin="dense"
              name="imageUrl"
              label="Featured Image URL"
              type="url"
              fullWidth
              value={formData.imageUrl}
              onChange={handleChange}
              required
              helperText="Enter a valid image URL (e.g., https://example.com/image.jpg)"
            />
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              startIcon={<SaveIcon />}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default NewsManagement; 